import { useEffect, useState } from "react";
import PublicShell, { useSiteContent } from "@/components/layout/PublicShell";
import { api, formatApiError } from "@/lib/api";
import { useFormSecurity } from "@/lib/useFormSecurity";
import { CheckCircle2, UploadCloud, FileText, Network } from "lucide-react";

const NETWORK_IMG =
  "https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b3Jrc3BhY2UlMjB0ZWNofGVufDB8fHx8MTc3Nzk1ODc3NHww&ixlib=rb-4.1.0&q=85";

const FALLBACK_DISCIPLINES = ["Program Manager", "Project Manager", "Business Analyst", "Developer", "Tester"];
const FALLBACK_AVAILABILITY = [
  "Available now",
  "Available within 2 weeks",
  "Available within 1 month",
  "Available within 3 months",
  "Not currently available",
];

export default function JoinNetworkPage() {
  const content = useSiteContent();
  const copy = content?.join_network || {};
  const [meta, setMeta] = useState({ disciplines: FALLBACK_DISCIPLINES, availability_options: FALLBACK_AVAILABILITY });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    discipline: "",
    rate: "",
    rate_type: "daily",
    availability: "",
    location: "",
    skills: "",
    website: "", // honeypot
  });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const { getSignals } = useFormSecurity();

  useEffect(() => {
    api.get("/candidates/meta").then((r) => {
      if (r.data?.disciplines?.length) setMeta(r.data);
    }).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!cv) return setError("Please attach your CV (PDF or DOCX).");
    const ext = cv.name.split(".").pop().toLowerCase();
    if (!["pdf", "docx"].includes(ext)) return setError("CV must be a PDF or DOCX file.");
    if (cv.size > 10 * 1024 * 1024) return setError("CV must be 10MB or smaller.");
    if (cv.size === 0) return setError("CV file is empty.");

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const signals = getSignals();
      fd.append("loaded_at", String(signals.loaded_at));
      fd.append("hb", signals.hb);
      fd.append("cv", cv);
      await api.post("/candidates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicShell>
      <section className="relative border-b border-border overflow-hidden" data-testid="network-hero">
        <div className="absolute inset-0 bg-dotgrid opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
            <div className="flex items-center gap-3 mb-6">
              <Network className="h-4 w-4 text-execution" />
              <span className="overline">Nexra Delivery Network · VMO-style model</span>
            </div>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.02] text-obsidian">
              {copy.title || "Nexra Delivery Network"}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">{copy.subtitle}</p>
            <p className="mt-6 text-base text-foreground/80 max-w-xl leading-relaxed">{copy.intro}</p>
          </div>
          <div className="lg:col-span-5 relative min-h-[240px] lg:min-h-[520px] border-l border-border overflow-hidden">
            <img src={NETWORK_IMG} alt="Network professional" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-obsidian/20 to-transparent" />
            <div className="absolute top-6 right-6 chip-dark">
              <span className="h-1.5 w-1.5 bg-execution rounded-full" />
              Curated · Outcome-focused
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section" data-testid="network-how">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4">How the network works</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {copy.model_title}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="border-t border-b border-border divide-y divide-border">
              {(copy.model_points || []).map((p, i) => (
                <li key={i} className="py-5 flex gap-6 items-start">
                  <span className="font-mono text-xs text-execution tracking-widest w-10 shrink-0 pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base lg:text-lg leading-snug">{p}</span>
                </li>
              ))}
            </ul>
            {copy.disciplines && (
              <div className="mt-8">
                <div className="overline mb-3">Disciplines we engage</div>
                <div className="flex flex-wrap gap-2">
                  {copy.disciplines.map((d) => (
                    <span key={d} className="chip">{d}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="border-t border-border bg-muted" data-testid="network-form-section">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          {done ? (
            <div className="border border-border p-10 lg:p-16 bg-white" data-testid="network-confirmation">
              <CheckCircle2 className="h-8 w-8 text-execution mb-6" />
              <h2
                className="font-heading text-2xl lg:text-4xl font-semibold tracking-tight max-w-2xl"
                data-testid="network-success-title"
              >
                {copy.success_title || "Thank you for joining the Nexra Delivery Network."}
              </h2>
              <p
                className="mt-6 text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl"
                data-testid="network-success-body"
              >
                {copy.success_body || "We have received your profile. Nexra will review your details and contact you when a suitable engagement aligns. No further action is required from you at this stage."}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="border border-border p-5 sm:p-6 lg:p-10 bg-white" data-testid="network-form">
              <div className="flex items-center gap-3 mb-6 sm:mb-8">
                <Network className="h-5 w-5 text-execution shrink-0" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight">Apply to the Delivery Network</h2>
              </div>
              {/* Honeypot */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                <label>Website</label>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <input className="input-swiss" required autoComplete="name" value={form.name} onChange={update("name")} data-testid="network-input-name" />
                </Field>
                <Field label="Email" required>
                  <input className="input-swiss" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" autoCorrect="off" required value={form.email} onChange={update("email")} data-testid="network-input-email" />
                </Field>
                <Field label="Phone" required>
                  <input className="input-swiss" type="tel" inputMode="tel" autoComplete="tel" required value={form.phone} onChange={update("phone")} data-testid="network-input-phone" />
                </Field>
                <Field label="Discipline" required>
                  <select className="input-swiss" required value={form.discipline} onChange={update("discipline")} data-testid="network-input-discipline">
                    <option value="">Select your discipline</option>
                    {meta.disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Rate" required>
                  <input className="input-swiss" required placeholder="e.g., $1200" inputMode="decimal" value={form.rate} onChange={update("rate")} data-testid="network-input-rate" />
                </Field>
                <Field label="Rate type" required>
                  <select className="input-swiss" required value={form.rate_type} onChange={update("rate_type")} data-testid="network-input-rate-type">
                    <option value="daily">Daily</option>
                    <option value="hourly">Hourly</option>
                  </select>
                </Field>
                <Field className="md:col-span-2" label="Availability" required>
                  <select className="input-swiss" required value={form.availability} onChange={update("availability")} data-testid="network-input-availability">
                    <option value="">Select availability</option>
                    {meta.availability_options.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </Field>
                <Field className="md:col-span-2" label="Location">
                  <input className="input-swiss" placeholder="e.g., London, UK — or 'Remote'" value={form.location} onChange={update("location")} data-testid="network-input-location" />
                </Field>
              </div>

              <Field className="mt-5" label="Skills / Experience" required>
                <textarea
                  className="input-swiss min-h-[160px] resize-y"
                  required
                  placeholder="Programs delivered, industries, tooling, leadership scope..."
                  value={form.skills}
                  onChange={update("skills")}
                  data-testid="network-input-skills"
                />
              </Field>

              <div className="mt-6">
                <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
                  CV upload <span className="text-execution">*</span>
                </span>
                <label
                  htmlFor="cv-upload"
                  data-testid="network-cv-dropzone"
                  className="flex items-center gap-4 border border-dashed border-border px-5 py-6 cursor-pointer hover:border-execution transition-colors bg-muted/30"
                >
                  <div className="h-12 w-12 bg-obsidian flex items-center justify-center shrink-0">
                    {cv ? <FileText className="h-5 w-5 text-execution" /> : <UploadCloud className="h-5 w-5 text-white" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading text-base font-medium truncate">
                      {cv ? cv.name : "Attach CV (PDF or DOCX — up to 10MB)"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {cv ? `${(cv.size / 1024).toFixed(0)} KB` : "Click to select file"}
                    </div>
                  </div>
                </label>
                <input
                  id="cv-upload"
                  type="file"
                  accept=".pdf,.docx"
                  className="hidden"
                  onChange={(e) => setCv(e.target.files?.[0] || null)}
                  data-testid="network-cv-input"
                />
              </div>

              {error && <div className="mt-4 text-sm text-destructive" data-testid="network-error">{error}</div>}

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                  You will receive an automated confirmation email.
                </p>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60 w-full sm:w-auto" data-testid="network-submit">
                  {submitting ? "Submitting..." : "Join Nexra Network"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
        {label}{required && <span className="text-execution"> *</span>}
      </span>
      {children}
    </label>
  );
}
